import { motion } from 'framer-motion';
import { cn } from '@/lib/cn';

/**
 * ProgressBar — animated bar used on Screen 5. Width animates with a
 * bouncy spring (spec called out cubic-bezier(.34,1.56,.64,1) for the
 * overshoot — Framer Motion's `type: 'spring'` with bounce=0.4 lands a
 * close visual match without a custom easing definition).
 *
 * The percent label inside the fill is hidden when the bar is too narrow
 * to host it (under 8% per the spec), preventing clipped digits.
 */
export interface ProgressBarProps {
  completed: number;
  total: number;
  className?: string;
}

export function ProgressBar({ completed, total, className }: ProgressBarProps) {
  const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const showLabel = pct >= 8;

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={completed}
      aria-label={`${completed} of ${total} days complete`}
      className={cn(
        'relative h-6 w-full overflow-hidden rounded-full bg-or-blue-100',
        className,
      )}
    >
      <motion.div
        initial={false}
        animate={{ width: `${pct}%` }}
        transition={{ type: 'spring', bounce: 0.4, duration: 0.6 }}
        className="absolute left-0 top-0 flex h-full items-center justify-end rounded-full bg-or-green-700 pr-2"
      >
        {showLabel ? (
          <span className="text-[11px] font-bold text-white tabular-nums">{pct}%</span>
        ) : null}
      </motion.div>
    </div>
  );
}
